<template>
  <AiLoading v-if="isPending || !loaded" label="Gathering comparable rentals nearby…" />

  <Text v-else-if="comps.length === 0" variant="body-medium" color="tertiary">
    No comparable rentals found for this property.
  </Text>

  <View v-else direction="column" gap="md">
    <!-- Your estimated rent: the number every comparable is held against, in the comparables' own card -->
    <section v-if="isEstimating || anchor != null" class="cmp-estimate">
      <div class="cmp-estimate__main">
        <PropertyPhotoPreview :image-url="coverImage" :size="thumbnailSize" />

        <div class="cmp-estimate__body">
          <span class="cmp-estimate__k">
            {{ isEstimating || estimate != null ? 'Your estimated rent' : 'Median comparable' }}
          </span>

          <template v-if="isEstimating">
            <span class="cmp-estimate__pending">Estimating…</span>
            <Skeleton width="11rem" height="0.875rem" />
          </template>
          <template v-else>
            <span class="cmp-estimate__price">
              ${{ formatPrice(anchor) }}
              <small>/mo</small>
            </span>
            <span v-if="rangeParts" class="cmp-estimate__range">
              <span class="cmp-nowrap">{{ rangeParts[0] }}</span>
              <span v-if="rangeParts[1]" class="cmp-sep">{{ separator }}</span>
              <span v-if="rangeParts[1]" class="cmp-nowrap">{{ rangeParts[1] }}</span>
            </span>
          </template>

          <span v-if="!isEstimating && confidenceLabel" class="cmp-estimate__meta">{{ confidenceLabel }}</span>
        </div>
      </div>

      <p v-if="!isEstimating && estimateSummary" class="cmp-estimate__summary">{{ estimateSummary }}</p>
    </section>

    <View direction="column" gap="sm">
      <Text variant="body-small" color="tertiary">{{ subhead }}</Text>

      <div class="cmp-list">
        <a
          v-for="comp in comps"
          :key="comp.id || comp.formattedAddress || ''"
          class="cmp-card"
          :href="zillowUrl(comp)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <PropertyPhotoPreview :image-url="streetViewUrl(comp)" :size="thumbnailSize" no-photo-on-error />

          <div class="cmp-card__body">
            <span class="cmp-card__addr">{{ streetLine(comp) }}</span>
            <!-- Only once the estimate is in: held against RentCast's number first, the chips would jump -->
            <Transition name="cmp-fade">
              <span v-if="isCompared" class="cmp-chip" :class="`is-${tier(comp)}`">{{ chipLabel(comp) }}</span>
            </Transition>

            <span class="cmp-card__line">
              <span class="cmp-card__price cmp-nowrap">
                ${{ formatPrice(comp.price) }}
                <small>/mo</small>
              </span>
              <template v-if="specsLabel(comp)">
                <span class="cmp-sep">{{ separator }}</span>
                <span>{{ specsLabel(comp) }}</span>
              </template>
            </span>

            <span v-if="marketLabel(comp)" class="cmp-card__market" :class="{ 'is-stale': isStale(comp) }">
              {{ marketLabel(comp) }}
            </span>
          </div>
        </a>
      </div>
    </View>
  </View>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { AiLoading, screenMobile, Skeleton, Text, View } from '@tallo/design-system';
import {
  ComparableRentalDto,
  usePropertyComparableRentalsQuery,
  usePropertyRentEstimateQuery,
  usePropertyStore,
} from '@tallo/investor/property';
import { PropertyPhotoPreview } from '@tallo/property';

const props = defineProps<{ limit?: number }>();

// How far from the anchor a comp must sit before we flag it below/above market.
const tierBand = 0.07;
// Comps older than this read as weak evidence and get a freshness caveat.
const staleDays = 180;
// RentCast counts a listing it saw only once as 1 day on the market, and a relisted one from its first
// listing: past these bounds its days on market say nothing about how fast the place rented
const maxMeaningfulDaysOnMarket = 365;
// Twice the 80px tile, for sharp thumbnails on high-density screens
const streetViewSize = '160x160';
// Real spaces, so a screen reader and a copied line read "$2,400 /mo · 2 BR", not "/mo·2 BR"
const separator = ' · ';
// Between a number and its unit, so a narrow card never ends a line on "1,400" and starts the next on "sqft"
const nbsp = '\u00a0';
// The 80px tile leaves a phone's card too little room for the address and the price
const thumbnailSize = computed(() => (screenMobile.value ? 'sm' : 'md'));

// Gate the loading state on `isPending` only (matches the other dashboard widgets): it's true just
// for the first load, when there's no data yet. Background refetches (e.g. on tab focus) leave the
// loaded comparables on screen and swap in fresh data, instead of flashing the loading state over it.
const { data, isPending } = usePropertyComparableRentalsQuery();
// `loaded` is false only while RentCast has not yet populated this property's data — we then keep
// showing the "gathering" state instead of the "no comparables" state. Defaults to true when the
// field is absent (e.g. a backend that predates this flag) so the widget never gets stuck gathering.
const loaded = computed(() => data.value?.loaded ?? true);

// The AI estimate weighs RentCast, Zillow, the unit's photos and street photos, so its first run on a
// unit takes a while: the comparables show meanwhile and the anchor says it is estimating. Should the
// request fail, RentCast's own estimate (from the comparables) stays the anchor.
const { data: rentEstimate, isPending: isEstimating } = usePropertyRentEstimateQuery();
const estimateSource = computed(() => (rentEstimate.value?.rent != null ? rentEstimate.value : data.value));

const estimate = computed(() => estimateSource.value?.rent ?? null);
const rangeLow = computed(() => estimateSource.value?.rentRangeLow ?? null);
const rangeHigh = computed(() => estimateSource.value?.rentRangeHigh ?? null);
// The AI's range is the speed trade-off (rents within two weeks / most worth asking if the owner can
// wait); RentCast's, the stand-in, is a plain margin
const isAiEstimate = computed(() => rentEstimate.value?.source === 'ai');
const estimateSummary = computed(() => (isAiEstimate.value ? rentEstimate.value!.summary : null));
const confidenceLabel = computed(() => {
  const confidence = isAiEstimate.value ? rentEstimate.value!.confidence : null;
  return confidence ? `${confidence[0].toUpperCase()}${confidence.slice(1)} confidence` : null;
});
// In parts, so a narrow card breaks the line between the two prices and never inside one
const rangeParts = computed(() => {
  if (estimate.value == null || rangeLow.value == null || rangeHigh.value == null) return null;

  return isAiEstimate.value
    ? [`Rent fast $${formatPrice(rangeLow.value)}`, `Hold out $${formatPrice(rangeHigh.value)}`]
    : [`Likely $${formatPrice(rangeLow.value)} – $${formatPrice(rangeHigh.value)}`];
});

const propertyStore = usePropertyStore();
const coverImage = computed(() => propertyStore.property?.coverImage ?? undefined);

const allComparables = computed(() => data.value?.comparables ?? []);

// The server drops the unit itself from its comparables; an older cached payload may still list it.
// Matched by address, not distance: the other units in its building sit at distance 0 and are the
// closest comparables there are.
const selfComp = computed(() => {
  const subjectAddress = data.value?.subject?.formattedAddress;

  return subjectAddress
    ? (allComparables.value.find((c) => isSameAddress(c.formattedAddress, subjectAddress)) ?? null)
    : null;
});

const comps = computed(() => {
  const list = allComparables.value
    .filter((c) => c !== selfComp.value && c.price != null)
    .sort((a, b) => (b.correlation ?? 0) - (a.correlation ?? 0));

  return props.limit ? list.slice(0, props.limit) : list;
});

// Anchor every comparison to the estimate; fall back to the median comp when RentCast gives no estimate.
const anchor = computed(() => estimate.value ?? median(comps.value.map((c) => c.price as number)));
const isCompared = computed(() => !isEstimating.value && anchor.value != null);

const commonBeds = computed(() => {
  const beds = comps.value.map((c) => c.bedrooms).filter((b): b is number => b != null);
  return beds.length && beds.every((b) => b === beds[0]) ? beds[0] : null;
});

const subhead = computed(() => {
  const n = comps.value.length;
  const bed = commonBeds.value ? `${commonBeds.value}-bed ` : '';
  const against = estimate.value != null ? 'your estimate' : 'the median';
  return `How ${n} nearby ${bed}rental${n === 1 ? '' : 's'} compare to ${against}`;
});

function median(nums: number[]): number | null {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function deltaRatio(comp: ComparableRentalDto): number | null {
  if (comp.price == null || anchor.value == null || anchor.value === 0) return null;
  return (comp.price - anchor.value) / anchor.value;
}

function tier(comp: ComparableRentalDto): 'below' | 'above' | 'in-line' {
  const ratio = deltaRatio(comp);
  if (ratio == null || Math.abs(ratio) <= tierBand) return 'in-line';
  return ratio < 0 ? 'below' : 'above';
}

function chipLabel(comp: ComparableRentalDto): string {
  const ratio = deltaRatio(comp);
  if (ratio == null || Math.abs(ratio) <= tierBand) return 'In line';
  return `${Math.round(Math.abs(ratio) * 100)}% ${ratio < 0 ? 'below' : 'above'}`;
}

function specsLabel(comp: ComparableRentalDto): string {
  const parts: string[] = [];

  if (comp.bedrooms != null) parts.push(comp.bedrooms === 0 ? 'Studio' : `${comp.bedrooms}${nbsp}BR`);
  if (comp.squareFootage != null) parts.push(`${formatPrice(comp.squareFootage)}${nbsp}sqft`);

  return parts.join(', ');
}

// How the listing fared where RentCast knows, how recently it was listed where it does not
function marketLabel(comp: ComparableRentalDto): string | null {
  return marketStatus(comp) ?? listedAgo(comp);
}

function marketStatus(comp: ComparableRentalDto): string | null {
  if (!comp.status) return null;

  const days = comp.daysOnMarket;
  const isStillListed = comp.status === 'Active';
  const isMeaningful = days != null && days <= maxMeaningfulDaysOnMarket && (isStillListed || days > 1);

  if (isStillListed) return isMeaningful ? `Listed ${days}${nbsp}day${days === 1 ? '' : 's'}` : 'Still listed';
  return isMeaningful ? `Off market after ${days}${nbsp}days` : 'Off market';
}

function listedAgo(comp: ComparableRentalDto): string | null {
  const days = comp.daysOld;
  if (days == null) return null;
  if (days < 30) return `Listed ${days}${nbsp}day${days === 1 ? '' : 's'} ago`;
  if (days < 365) return `Listed ${Math.round(days / 30.44)}${nbsp}mo ago`;
  return `Listed ${Math.floor(days / 365)}+${nbsp}yr ago`;
}

// Only an age caveat: a comparable whose status is known says how it fared instead
function isStale(comp: ComparableRentalDto): boolean {
  return !comp.status && comp.daysOld != null && comp.daysOld >= staleDays;
}

// The comparable's own address line, its unit included: the city, state and ZIP are the unit's too
function streetLine(comp: ComparableRentalDto): string {
  const address = comp.formattedAddress ?? '';
  const place = [comp.city, [comp.state, comp.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return place && address.endsWith(`, ${place}`) ? address.slice(0, -(place.length + 2)) : street(address);
}

function street(address: string | null): string {
  return address ? address.split(',')[0].trim() : '';
}

// However each spells its unit: `Apt 2`, `#2` and `Unit 2` are the same home
function isSameAddress(a: string | null, b: string): boolean {
  const normalize = (address: string) =>
    address
      .toLowerCase()
      .replace(/#\s*|\b(?:apt|apartment|unit|ste|suite)\b\.?\s*/g, 'unit ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  return a != null && normalize(a) === normalize(b);
}

function formatPrice(value: number | null): string {
  return value == null ? '' : value.toLocaleString('en-US');
}

// RentCast comparables carry no photos: Street View shows the building. Where Google has no imagery the
// request fails and the tile reads as no photo.
function streetViewUrl(comp: ComparableRentalDto): string | undefined {
  const key = process.env.GOOGLE_MAPS_KEY;
  if (!comp.formattedAddress || !key) return undefined;

  const params = new URLSearchParams({
    size: streetViewSize,
    location: comp.formattedAddress,
    fov: '80',
    source: 'outdoor',
    return_error_code: 'true',
    key,
  });

  return `https://maps.googleapis.com/maps/api/streetview?${params}`;
}

// RentCast comparables don't include a public listing URL, so link to a Zillow address search.
function zillowUrl(comp: ComparableRentalDto): string | undefined {
  if (!comp.formattedAddress) return undefined;
  return `https://www.zillow.com/homes/${encodeURIComponent(comp.formattedAddress)}_rb/`;
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// Figma: white cards on the white card, set apart by a soft shadow rather than a border
%cmp-surface {
  background: var(--t-color-neutral-white);
  border-radius: var(--t-border-radius-lg);
  box-shadow: var(--t-shadow-sm);
}

.cmp-sep {
  white-space: pre;
  color: var(--t-text-color-tertiary);
}

.cmp-nowrap {
  white-space: nowrap;
}

/* Your estimated rent */
.cmp-estimate {
  @extend %cmp-surface;

  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
  padding: var(--t-spacing-md);
}

.cmp-estimate__main {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-md);
}

.cmp-estimate__body {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.cmp-estimate__k {
  font: var(--t-typography-label-sm-font);
  color: var(--t-text-color-secondary);
}

// The hero number of the widget
.cmp-estimate__price {
  font: var(--t-typography-heading-h2-font);
  color: var(--t-text-color-primary);
  font-variant-numeric: tabular-nums;

  small {
    margin-left: 0.125rem;
    font: var(--t-typography-body-md-font);
    color: var(--t-text-color-secondary);
  }
}

.cmp-estimate__pending {
  font: var(--t-typography-heading-h4-font);
  color: var(--t-text-color-tertiary);
}

// The range is part of the answer, not a footnote
.cmp-estimate__range {
  font: var(--t-typography-label-md-font);
  color: var(--t-text-color-primary);
  font-variant-numeric: tabular-nums;
}

.cmp-estimate__meta {
  font: var(--t-typography-body-sm-font);
  color: var(--t-text-color-tertiary);
}

.cmp-estimate__summary {
  margin: 0;
  padding-top: var(--t-spacing-md);
  border-top: 1px solid var(--t-color-neutral-100);
  font: var(--t-typography-body-sm-font);
  color: var(--t-text-color-secondary);
}

/* Comparable cards: photo, then address, price and size, and how the listing fared */
.cmp-list {
  display: grid;
  // One column in the dashboard's narrow column and on a phone, two where both fit
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 20rem), 1fr));
  gap: var(--t-spacing-md);
}

.cmp-card {
  @extend %cmp-surface;

  display: flex;
  align-items: center;
  gap: var(--t-spacing-md);
  min-width: 0;
  padding: var(--t-spacing-md);
  text-decoration: none;
  color: inherit;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    box-shadow: var(--t-shadow-md);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }
}

.cmp-card__body {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'addr chip'
    'line line'
    'market market';
  align-items: center;
  column-gap: var(--t-spacing-sm);
  row-gap: 0.25rem;
  min-width: 0;

  // No room beside a phone's address: the chip closes the market line instead
  @include mobile {
    grid-template-areas:
      'addr addr'
      'line line'
      'market chip';
  }
}

// Two lines at most, so a long address with its unit is read rather than cut
.cmp-card__addr {
  grid-area: addr;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  font: var(--t-typography-heading-h4-font);
  color: var(--t-text-color-primary);
}

.cmp-card__line {
  grid-area: line;
  font: var(--t-typography-body-md-font);
  color: var(--t-text-color-secondary);
  font-variant-numeric: tabular-nums;
}

.cmp-card__price {
  color: var(--t-text-color-primary);

  small {
    font: inherit;
    color: var(--t-text-color-secondary);
  }
}

// Figma's demand line ("20 leads"): here, how the listing fared on the market
.cmp-card__market {
  grid-area: market;
  margin-top: var(--t-spacing-xs);
  font: var(--t-typography-body-sm-font);
  color: var(--t-text-color-tertiary);

  &.is-stale {
    color: var(--t-color-yellow-700);
  }
}

.cmp-chip {
  grid-area: chip;
  justify-self: end;
  flex-shrink: 0;
  font: var(--t-typography-label-sm-font);
  white-space: nowrap;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: var(--t-color-neutral-50);
  color: var(--t-color-neutral-700);

  &.is-below {
    background: var(--t-color-green-50);
    color: var(--t-color-green-700);
  }

  &.is-above {
    background: var(--t-color-red-50);
    color: var(--t-color-red-700);
  }
}

.cmp-fade-enter-active {
  transition: opacity 0.2s ease;
}

.cmp-fade-enter-from {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .cmp-card,
  .cmp-fade-enter-active {
    transition: none;
  }

  .cmp-card:hover {
    transform: none;
  }
}
</style>
